import { useMemo, useState } from "react";

// Count options that fit the available pool: the base steps that are <= the
// pool (capped at maxCount), plus the pool size itself as a final "all" step.
function optionsFor(available, base, maxCount) {
  const cap = Math.min(available, maxCount);
  const opts = base.filter((o) => o <= cap);
  if (cap > 0 && !opts.includes(cap)) opts.push(cap);
  return opts.sort((a, b) => a - b);
}

// The largest option that doesn't exceed the user's desired count (or, if none
// fit, the smallest available option). Pure — no effect, no extra render.
function clampToOptions(desired, options) {
  if (options.includes(desired)) return desired;
  const fit = options.filter((o) => o <= desired);
  return fit.length ? fit[fit.length - 1] : options[options.length - 1] || 0;
}

// Renders the subject chips (with per-subject counts) and the Questions count
// selector inside the module's Rails practice form. Shows how many questions the
// current selection holds and clamps the requested count so the candidate never
// asks for more than exists. Empty subjects are shown disabled.
export default function ModulePracticeBuilder({
  subjects = [],
  total = 0,
  count_options = [5, 10, 20, 30, 40],
  default_count = 20,
  max_count = 100,
}) {
  const [selected, setSelected] = useState(() => new Set());
  // Holds only the candidate's raw pick; the effective count is derived from it
  // and the current options, so it self-corrects as availability shifts.
  const [desiredCount, setDesiredCount] = useState(default_count);

  const available = useMemo(() => {
    if (selected.size === 0) return total;
    return subjects
      .filter((s) => selected.has(s.id))
      .reduce((sum, s) => sum + s.count, 0);
  }, [selected, subjects, total]);

  const options = useMemo(
    () => optionsFor(available, count_options, max_count),
    [available, count_options, max_count]
  );

  const count = clampToOptions(desiredCount, options);

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <>
      {subjects.length > 1 && (
        <div className="mod-practice__field">
          <span className="mod-practice__label">
            Narrow to subjects <span className="mod-practice__hint">(optional)</span>
          </span>
          <div className="mod-practice__chips">
            {subjects.map((subject) => {
              const empty = subject.count === 0;
              return (
                <label
                  key={subject.id}
                  className={`mod-practice__chip${empty ? " --disabled" : ""}`}
                >
                  <input
                    type="checkbox"
                    name="subject_ids[]"
                    value={subject.id}
                    disabled={empty}
                    checked={selected.has(subject.id)}
                    onChange={() => toggle(subject.id)}
                  />
                  {subject.name}
                  <span className="mod-practice__chip-count">· {subject.count}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      <div className="mod-practice__avail" aria-live="polite">
        <strong>{available}</strong> {available === 1 ? "question" : "questions"} available
        {available > 0 && <> · practising {Math.min(count, available)}</>}
      </div>

      <div className="mod-practice__field">
        <label className="mod-practice__label" htmlFor="practice-count">
          Questions
        </label>
        <div className="mod-practice__select mod-practice__select--narrow">
          <select
            id="practice-count"
            name="count"
            className="cand-input"
            value={count}
            onChange={(e) => setDesiredCount(Number(e.target.value))}
          >
            {options.map((o) => (
              <option key={o} value={o}>
                {o === available ? `All (${o})` : o}
              </option>
            ))}
          </select>
        </div>
      </div>
    </>
  );
}
